import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { machines } from '../../lib/api/resources.ts'
import type { MachineResponse } from '../../lib/api/types.ts'
import { useAuth } from '../../lib/auth/AuthContext.tsx'
import { isAwaitingAgent, openCommandIdOf } from '../../lib/commands/semantics.ts'
import {
  isUninstallOpen,
  needsReissue,
  UNINSTALL_KIND,
  uninstallBadge,
  uninstallOutcome,
  uninstallTriggerLabel,
  uninstallTriggerOf,
} from '../../lib/commands/uninstall.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { ShortId } from '../primitives/CopyValue.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { useToast } from '../primitives/Toast.tsx'
import { ReleaseStatus } from '../commands/CommandState.tsx'
import { machineLabel } from './ReleaseHost.tsx'

/** Selo da desinstalação na máquina, pelo `state`. "Agente desinstalado" só em `executed`. */
export function UninstallBadge({ command }: { command: AgentCommand }) {
  const p = uninstallBadge(command.state)
  return (
    <Badge tone={p.tone} dot>
      {p.label}
    </Badge>
  )
}

/** O aviso de desinstalação dentro da `consequence` de um diálogo de revogar/remover. */
export function UninstallWarning({ children }: { children: ReactNode }) {
  return <strong className="mt-1.5 block font-medium text-crit">{children}</strong>
}

/** Gatilho e autoria da desinstalação. */
function UninstallOrigin({ command }: { command: AgentCommand }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-micro text-fg-muted">
      <dt>gatilho</dt>
      <dd className="text-fg-secondary">{uninstallTriggerLabel(uninstallTriggerOf(command))}</dd>
      <dt>pedido por</dt>
      <dd>{command.requested_by === null ? '—' : <ShortId id={command.requested_by} label="requested_by" />}</dd>
      {command.reason !== null && (
        <>
          <dt>justificativa</dt>
          <dd className="break-words text-fg-secondary">{command.reason}</dd>
        </>
      )}
    </dl>
  )
}

/** Andamento de UMA desinstalação: selo, gatilho, quem pediu e o desfecho. */
export function UninstallStatus({ command }: { command: AgentCommand }) {
  const outcome = uninstallOutcome(command.state)
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <UninstallBadge command={command} />
      </div>
      <UninstallOrigin command={command} />
      <ReleaseStatus command={command} outcome={outcome} />
    </div>
  )
}

/**
 * O estado da desinstalação da máquina e, para `platform_admin`, o botão de
 * emitir/reemitir. Puramente apresentacional, para o render check.
 */
export function UninstallOverview({
  last,
  isAdmin,
  onIssue,
  onTrack,
}: {
  /** A desinstalação mais recente da máquina, se houver. */
  last: AgentCommand | null
  isAdmin: boolean
  onIssue: () => void
  onTrack: (id: string) => void
}) {
  const open = isUninstallOpen(last)

  return (
    <div className="space-y-3">
      {last === null ? (
        <p className="text-sm text-fg-secondary">Nenhuma desinstalação emitida para esta máquina.</p>
      ) : (
        <UninstallStatus command={last} />
      )}

      {open && last !== null && (
        <Notice tone="neutral">
          Desinstalação em andamento.{' '}
          <button type="button" className="underline" onClick={() => onTrack(last.id)}>
            Acompanhar esse comando
          </button>
        </Notice>
      )}

      {/* Só administrador emite. Com uma aberta, acompanhar — o servidor recusaria a segunda. */}
      {isAdmin && !open && (
        <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
          {needsReissue(last) && (
            <span className="mr-auto text-micro text-fg-muted">A última não chegou ao fim: o agente segue instalado.</span>
          )}
          <Button variant="danger" onClick={onIssue}>
            Desinstalar agente
          </Button>
        </div>
      )}
    </div>
  )
}

type Phase =
  | { kind: 'overview' }
  | { kind: 'form' }
  | { kind: 'tracking'; id: string; initial: AgentCommand | null }

/**
 * Desinstalação do agente da máquina: o andamento da última, e a reemissão
 * (`POST /machines/{id}/commands/uninstall`, só `platform_admin`).
 *
 * Serve também à máquina REMOVIDA — é nela que o operador procura depois de
 * remover. Não usa o catálogo do console: com máquina removida ele responde 404.
 */
export function MachineUninstallDialog({
  machine,
  track = null,
  onClose,
  onChanged,
}: {
  machine: MachineResponse
  /** Abre direto no acompanhamento deste comando (vindo do histórico). */
  track?: AgentCommand | null
  onClose: () => void
  /** Uma desinstalação foi emitida ou terminou. Quem chama recarrega a lista. */
  onChanged: () => void
}) {
  const { isAdmin } = useAuth()
  const [phase, setPhase] = useState<Phase>(
    track === null ? { kind: 'overview' } : { kind: 'tracking', id: track.id, initial: track },
  )

  const lastFetcher = useCallback(
    (signal: AbortSignal) => machines.commands(machine.id, { pageSize: 1, kind: UNINSTALL_KIND }, signal),
    [machine.id],
  )
  const last = useResource(lastFetcher)

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Desinstalação do agente"
      description={
        <>
          <span className="font-medium text-fg">{machineLabel(machine)}</span>
          {machine.deletedAt !== null && <> · removida</>}
        </>
      }
      wide
    >
      {phase.kind === 'overview' ? (
        last.error !== null ? (
          <ErrorState error={last.error} onRetry={last.reload} />
        ) : last.data === null ? (
          <LoadingRow label="Lendo a última desinstalação…" />
        ) : (
          <UninstallOverview
            last={last.data.items[0] ?? null}
            isAdmin={isAdmin}
            onIssue={() => setPhase({ kind: 'form' })}
            onTrack={(id) => setPhase({ kind: 'tracking', id, initial: null })}
          />
        )
      ) : phase.kind === 'form' ? (
        <UninstallForm
          machine={machine}
          isAdmin={isAdmin}
          onAccepted={(command) => {
            setPhase({ kind: 'tracking', id: command.id, initial: command })
            onChanged()
          }}
          onAlreadyOpen={(id) => setPhase({ kind: 'tracking', id, initial: null })}
          onCancel={() => setPhase({ kind: 'overview' })}
        />
      ) : (
        <UninstallTracking
          key={phase.id}
          machine={machine}
          commandId={phase.id}
          initial={phase.initial}
          isAdmin={isAdmin}
          onFinished={onChanged}
          onReissue={() => setPhase({ kind: 'form' })}
          onClose={onClose}
        />
      )}
    </Dialog>
  )
}

/** Os 409 de "sem agente" ganham o contexto da desinstalação; o resto, a frase de sempre. */
function uninstallErrorText(error: unknown): string {
  if (error instanceof AthenaError) {
    if (error.code === 'endpoint_has_no_agent') {
      return 'A máquina não tem agente vinculado: não há o que desinstalar.'
    }
    if (error.code === 'agent_not_active') {
      return 'O agente desta máquina não está ativo (suspenso ou revogado).'
    }
  }
  return describeError(error)
}

function UninstallForm({
  machine,
  isAdmin,
  onAccepted,
  onAlreadyOpen,
  onCancel,
}: {
  machine: MachineResponse
  isAdmin: boolean
  onAccepted: (command: AgentCommand) => void
  onAlreadyOpen: (id: string) => void
  onCancel: () => void
}) {
  const action = useAction()
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const disabled = !isAdmin || reason.trim() === '' || !confirmed

  const submit = (): void => {
    if (disabled) return
    action.run(async () => {
      try {
        onAccepted(await machines.uninstall(machine.id, { reason: reason.trim() }))
      } catch (cause) {
        // Já há uma aberta (talvez de um gatilho automático): leve o operador até ela.
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            onAlreadyOpen(id)
            return
          }
        }
        throw cause
      }
    })
  }

  return (
    <div className="space-y-3">
      <Notice tone="crit">
        <strong>Irreversível:</strong> o Argos de <strong>{machineLabel(machine)}</strong> se desinstala e apaga os
        dados locais.
      </Notice>

      {!isAdmin && <Notice tone="crit">Desinstalar o agente exige o papel de administrador.</Notice>}

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">
          Justificativa <span className="text-fg-muted">*</span>
        </span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={512}
          rows={3}
          required
          autoFocus
          className="w-full border border-line-strong bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted"
          placeholder="ex.: contrato encerrado, a desinstalação anterior expirou — ticket OPS-812"
        />
      </label>

      <label className="flex items-start gap-2 text-sm text-fg-secondary">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          className="mt-0.5 size-3.5 shrink-0"
          style={{ accentColor: 'var(--c-accent)' }}
        />
        <span>
          Confirmo que <span className="font-medium text-fg">{machineLabel(machine)}</span> deixa de ser
          monitorada.
        </span>
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        {action.error !== null && (
          <p role="alert" className="mr-auto text-sm leading-snug text-crit">
            {uninstallErrorText(action.error)}
          </p>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Voltar
        </Button>
        <Button variant="danger" busy={action.busy} disabled={disabled} onClick={submit}>
          Desinstalar agente
        </Button>
      </div>
    </div>
  )
}

function UninstallTracking({
  machine,
  commandId,
  initial,
  isAdmin,
  onFinished,
  onReissue,
  onClose,
}: {
  machine: MachineResponse
  commandId: string
  initial: AgentCommand | null
  isAdmin: boolean
  onFinished: () => void
  onReissue: () => void
  onClose: () => void
}) {
  const toast = useToast()
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  // Só anuncia se a confirmação aconteceu DIANTE do operador.
  const sawAwaiting = useRef(false)
  const announced = useRef(false)

  const state = command?.state ?? null
  if (state !== null && isAwaitingAgent(state)) sawAwaiting.current = true
  const terminal = state !== null && !isAwaitingAgent(state)

  useEffect(() => {
    if (!terminal || announced.current || !sawAwaiting.current) return
    announced.current = true
    if (state === 'executed') toast.success(`O agente de ${machineLabel(machine)} começou a se desinstalar.`)
    onFinished()
    // Só a transição para terminal dispara; `announced` evita repetir.
  }, [terminal])

  const canReissue = isAdmin && (state === 'failed' || state === 'expired' || state === 'cancelled')

  return (
    <div className="space-y-3">
      {command === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <>
          <UninstallStatus command={command} />
          {error !== null && (
            <Notice tone="warn">
              Não foi possível atualizar o estado: {describeError(error)}{' '}
              <button type="button" className="underline" onClick={refresh}>
                Tentar de novo
              </button>
            </Notice>
          )}
        </>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        <span className="mr-auto font-mono text-micro text-fg-muted">comando {commandId}</span>
        {canReissue && (
          <Button variant="danger" onClick={onReissue}>
            Desinstalar agente
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  )
}
